import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { ArrowRight, CheckCircle2, Building2, Layers, AlertCircle, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { formatCategoryLabel } from '../utils/normalizeAcronyms';

interface SpaceAssignmentProps {
  companyId: string;
  accessToken: string;
  onComplete: () => void;
}

interface ConfigItem {
  id: string;
  name: string;
  category: string;
  spaceId: string | null;
  isGlobal?: boolean;
}

interface Space {
  id: string;
  name: string;
  key?: string;
  type: string;
}

export function SpaceAssignment({ companyId, accessToken, onComplete }: SpaceAssignmentProps) {
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [unassignedItems, setUnassignedItems] = useState<ConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState<Record<string, string>>({}); // itemId -> spaceId
  const [globals, setGlobals] = useState<Record<string, boolean>>({}); // itemId -> isGlobal
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [companyId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Fetch spaces
      const spacesRes = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/space-discovery/${companyId}/spaces`,
        { headers: { 'Authorization': `Bearer ${accessToken}` } }
      );
      
      // Fetch config items
      const itemsRes = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${companyId}`,
        { headers: { 'Authorization': `Bearer ${accessToken}` } }
      );

      if (spacesRes.ok && itemsRes.ok) {
        const spacesData = await spacesRes.json();
        const itemsData = await itemsRes.json();

        setSpaces(spacesData.spaces || []);
        
        // Filter for unassigned items only
        const allItems = itemsData.configItems || [];
        const unassigned = allItems.filter((item: ConfigItem) => !item.spaceId);
        setUnassignedItems(unassigned);
      }
    } catch (error) {
      console.error('Error loading assignment data:', error);
      toast.error('Failed to load spaces and items');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = (itemId: string, spaceId: string) => {
    setAssignments(prev => ({
      ...prev,
      [itemId]: spaceId
    }));
    
    // If assigned to a space, it can't be global (unless we allow global items to also reside in a space? Usually global means cross-space)
    // For now, let's assume global = no spaceId, but visible everywhere.
    // If selected spaceId is "global", handle that.
    if (spaceId === 'global') {
        setGlobals(prev => ({ ...prev, [itemId]: true }));
        setAssignments(prev => {
            const copy = { ...prev };
            delete copy[itemId]; // Remove space assignment if global
            return copy;
        });
    } else {
        setGlobals(prev => {
            const copy = { ...prev };
            delete copy[itemId];
            return copy;
        });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Prepare bulk updates
      const updates = [];
      
      // Items assigned to specific spaces
      for (const [itemId, spaceId] of Object.entries(assignments)) {
        if (spaceId !== 'global') {
            updates.push({
                itemId,
                updates: { spaceId }
            });
        }
      }
      
      // Global items
      for (const [itemId, isGlobal] of Object.entries(globals)) {
        if (isGlobal) {
             updates.push({
                itemId,
                updates: { isGlobal: true, spaceId: null } // Global items have null spaceId
            });
        }
      }

      if (updates.length === 0) {
        onComplete();
        return;
      }

      // We need to send these one by one or batch them if the API supports it. 
      // The bulk-update endpoint takes { itemIds: [], updates: {} }.
      // Since each item might have a *different* spaceId, we can't use a single bulk call for everything.
      // We need to group by spaceId.
      
      const updatesBySpace: Record<string, string[]> = {}; // spaceId -> itemIds[]
      const globalItems: string[] = [];

      for (const [itemId, spaceId] of Object.entries(assignments)) {
          if (!updatesBySpace[spaceId]) updatesBySpace[spaceId] = [];
          updatesBySpace[spaceId].push(itemId);
      }

      for (const [itemId, isGlobal] of Object.entries(globals)) {
          if (isGlobal) globalItems.push(itemId);
      }

      // Execute updates
      const promises = [];

      // 1. Grouped Space Assignments
      for (const [spaceId, itemIds] of Object.entries(updatesBySpace)) {
          promises.push(
              fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/config-items/bulk-update`, {
                  method: 'POST',
                  headers: {
                      'Authorization': `Bearer ${accessToken}`,
                      'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                      itemIds,
                      updates: { spaceId }
                  })
              })
          );
      }

      // 2. Global Items
      if (globalItems.length > 0) {
          promises.push(
              fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/config-items/bulk-update`, {
                  method: 'POST',
                  headers: {
                      'Authorization': `Bearer ${accessToken}`,
                      'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                      itemIds: globalItems,
                      updates: { isGlobal: true, spaceId: null }
                  })
              })
          );
      }

      await Promise.all(promises);
      
      toast.success(`Assigned ${updates.length} items successfully`);
      onComplete();

    } catch (error) {
      console.error('Error saving assignments:', error);
      toast.error('Failed to save assignments');
    } finally {
      setSaving(false);
    }
  };

  const categories = Array.from(new Set(unassignedItems.map(i => i.category)));

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <Card className="border-blue-200 bg-blue-50/50">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg text-blue-700">
              <Layers className="size-6" />
            </div>
            <div>
              <CardTitle>Map Scope to Spaces</CardTitle>
              <CardDescription>
                Sort your {unassignedItems.length} unassigned config items into the newly created Jira Spaces.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left: Unassigned Items */}
        <div className="md:col-span-2 space-y-6">
            {categories.map(category => {
                const items = unassignedItems.filter(i => i.category === category);
                return (
                    <Card key={category}>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium uppercase text-muted-foreground flex items-center gap-2">
                                <Layers className="size-4" />
                                {formatCategoryLabel(category)}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            {items.map(item => (
                                <div key={item.id} className="flex items-center justify-between p-3 border rounded-lg bg-white hover:shadow-sm transition-shadow">
                                    <div className="flex-1">
                                        <div className="font-medium">{item.name}</div>
                                        <div className="text-xs text-muted-foreground truncate">{item.id}</div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center gap-2">
                                            <Checkbox 
                                                id={`global-${item.id}`} 
                                                checked={!!globals[item.id]}
                                                onCheckedChange={(checked) => {
                                                    if (checked) {
                                                        handleAssign(item.id, 'global');
                                                    } else {
                                                        const copy = { ...globals };
                                                        delete copy[item.id];
                                                        setGlobals(copy);
                                                        setAssignments(prev => {
                                                            const p = { ...prev };
                                                            delete p[item.id];
                                                            return p;
                                                        });
                                                    }
                                                }}
                                            />
                                            <label htmlFor={`global-${item.id}`} className="text-xs cursor-pointer flex items-center gap-1 text-muted-foreground">
                                                <Globe className="size-3" /> Global
                                            </label>
                                        </div>

                                        <Select 
                                            value={assignments[item.id] || ''} 
                                            onValueChange={(val) => handleAssign(item.id, val)}
                                            disabled={!!globals[item.id]}
                                        >
                                            <SelectTrigger className="w-[200px]">
                                                <SelectValue placeholder="Select Space..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {spaces.map(space => (
                                                    <SelectItem key={space.id} value={space.id}>
                                                        {space.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                );
            })}

            {unassignedItems.length === 0 && (
                 <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-lg">
                    <CheckCircle2 className="size-12 mx-auto mb-3 text-green-500" />
                    <p className="font-medium">All items assigned.</p>
                </div>
            )}
        </div>

        {/* Right: Spaces Summary */}
        <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase">Available Spaces</h3>
            {spaces.map(space => {
                const assignedCount = Object.values(assignments).filter(id => id === space.id).length;
                return (
                    <Card key={space.id} className="border-l-4 border-l-blue-500">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                                <div className="font-semibold">{space.name}</div>
                                <Badge variant="outline">{space.type}</Badge>
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                                <Layers className="size-3" />
                                {assignedCount} items assigned
                            </div>
                        </CardContent>
                    </Card>
                );
            })}
             
            <Card className="border-l-4 border-l-purple-500">
                <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold">Global / Shared</div>
                        <Badge variant="outline">Cross-Project</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                         <Globe className="size-3" />
                         {Object.keys(globals).length} items marked global
                    </div>
                </CardContent>
            </Card>

            <Button 
                className="w-full mt-4" 
                size="lg" 
                onClick={handleSave}
                disabled={saving || (Object.keys(assignments).length === 0 && Object.keys(globals).length === 0)}
            >
                {saving ? 'Saving Assignments...' : 'Confirm Assignments'}
                {!saving && <ArrowRight className="ml-2 size-4" />}
            </Button>
        </div>
      </div>
    </div>
  );
}